import { AlertTriangle, UserCheck } from 'lucide-react';
import { Modal } from './Modal';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = botão vermelho, warning = botão laranja/âmbar, success = botão verde, default = botão navy/azul */
  variant?: 'danger' | 'warning' | 'default' | 'success';
  onCancel: () => void;
  onConfirm: () => void;
  /** Nível de profundidade para modais aninhados (default: 2) */
  depth?: number;
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  onCancel,
  onConfirm,
  depth = 2,
}: ConfirmModalProps) {
  const getButtonClass = () => {
    if (variant === 'danger') return 'btn-danger-solid';
    return 'btn-save';
  };

  const getButtonStyle = () => {
    if (variant === 'success') return { background: '#16a34a', color: 'white', border: 'none' };
    if (variant === 'warning') return { background: '#f59e0b', color: 'white', border: 'none' };
    return undefined;
  };

  const getIconBackground = () => {
    if (variant === 'danger') return 'rgba(220, 38, 38, 0.1)';
    if (variant === 'warning') return 'rgba(245, 158, 11, 0.12)';
    if (variant === 'success') return 'rgba(22, 163, 74, 0.1)';
    return 'rgba(59, 130, 246, 0.1)';
  };

  const renderIcon = () => {
    if (variant === 'danger') return <AlertTriangle size={22} color="#dc2626" strokeWidth={2.2} />;
    if (variant === 'warning') return <AlertTriangle size={22} color="#d97706" strokeWidth={2.2} />;
    if (variant === 'success') return <UserCheck size={22} color="#16a34a" strokeWidth={2.2} />;
    return null;
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      depth={depth}
      // data-lab-* → instrumentação para o Lab executor detectar tipo de dialog
      data-lab-dialog-type="confirmation"
      footer={
        <>
          {/* data-lab-safe-action: botão SEGURO — executor deve clicar aqui ao detectar este dialog */}
          <button type="button" className="btn-cancel" onClick={onCancel} data-lab-safe-action="true">
            {cancelLabel}
          </button>
          {/* data-lab-confirm-action: botão DESTRUTIVO — executor NUNCA clica aqui autonomamente */}
          <button
            type="button"
            className={getButtonClass()}
            style={getButtonStyle()}
            onClick={onConfirm}
            data-lab-confirm-action="true"
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {(variant === 'danger' || variant === 'warning' || variant === 'success') && (
          <div
            style={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: 10,
              background: getIconBackground(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden
          >
            {renderIcon()}
          </div>
        )}
        <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.55, color: 'var(--text-main, #374151)' }}>{message}</p>
      </div>
    </Modal>
  );
}
